import React, {
  memo,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CategoryItem,
  CategoryNavigationItems,
} from './CategoryNavigationItems/CategoryNavigationItems';
import {NavigationFlyoutContentWrapper} from './NavigationFlyoutContent/NavigationFlyoutContentWrapper';

import {CategoryIdentifier} from '@aboutyou/grpc-clients/tadarida/aysa_api/entities/categories/v1/category_identifier.types';
import {Flyout} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/navigation_bar.types';
import {GetHeaderBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.types';
import {
  COLORS,
  GenericIconName,
  HandleClickOutside,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import {useIsHintCovered} from '../../../features/ShopHint/hooks/useIsHintCovered';
import {NAVIGATION_HEIGHT} from '../constants';
import {RowSectionStyled} from '../RowSectionStyled';
import {useSearchBarOpenedState} from '../SearchBar/hooks/useSearchBarExpander';
import {SearchSuggestions} from '../SearchBar/SearchSuggestions';
import {useCloseFlyoutContentOnRouteChange} from './hooks/useCloseFlyoutContentOnRouteChange';
import {useHeaderHints} from './hooks/useHeaderHints';
import {NavigationFlyoutContents} from './NavigationFlyoutContent/NavigationFlyoutContents';

export type NavigationItemsType = Omit<Flyout, 'containers'> | undefined;

export type TouchOrMouseEventHandler<Element = HTMLElement> =
  React.EventHandler<React.MouseEvent<Element> | React.TouchEvent<Element>>;

const checkIfEqual = (
  first?: CategoryIdentifier,
  second?: CategoryIdentifier,
) => {
  if (first?.identifier?.$case === 'id' && second?.identifier?.$case === 'id') {
    return first.identifier.id === second.identifier.id;
  }
  if (
    first?.identifier?.$case === 'legacyPath' &&
    second?.identifier?.$case === 'legacyPath'
  ) {
    return first.identifier.legacyPath === second.identifier.legacyPath;
  }

  return false;
};

const TopCategoryNavigationView: React.FC<GetHeaderBulkResponse['navBar']> = ({
  items,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const {setIsHintCovered} = useIsHintCovered();
  const {activeNavItemIndex, openFlyout, closeFlyout} = useActiveFlyout();
  useCloseFlyoutContentOnRouteChange(closeFlyout);

  const onEnterNavigationItem = useCallback(
    (index: number) => () => openFlyout(index),
    [openFlyout],
  );

  /**
   * This enhances the hints on the client with user specific hints
   */
  const headerHints = useHeaderHints();

  const mappedItems: CategoryItem[] = useMemo(
    () =>
      items.map(item => {
        const mappedItem: CategoryItem = {
          ...item,
        };

        if (headerHints.data && headerHints.data?.type) {
          const hint = headerHints.data?.categories.find(category =>
            checkIfEqual(category, item.category),
          );
          if (hint) {
            mappedItem.icon = {
              'data-testid': 'couponIcon',
              asset:
                headerHints.data.type.$case === 'coupon'
                  ? GenericIconName.CouponNav
                  : GenericIconName.Sale,
              color:
                headerHints.data.type.$case === 'coupon'
                  ? COLORS.PrimaryCoupon
                  : headerHints.data.type.discount.discountColor,
            };
          }
          return mappedItem;
        }

        // we only want those sale icons if there are no icons coming back from the header hints endpoint
        if (item.saleIcon && !headerHints.data?.type) {
          mappedItem.icon = {
            'data-testid': 'saleIcon',
            asset: GenericIconName.Sale,
            color: item.saleIcon.color.hex,
          };
        }

        return mappedItem;
      }),
    [headerHints.data, items],
  );

  const {isSearchBarExpanded, expandSearchBar, collapseSearchBar} =
    useSearchBarOpenedState();

  return (
    <TopCategoryNavigationViewWrapper
      ref={wrapperRef}
      onMouseLeave={closeFlyout}
      data-testid="headerNavigation"
    >
      {activeNavItemIndex !== undefined && (
        <HandleClickOutside wrapperRef={wrapperRef} onClose={closeFlyout} />
      )}

      <Wrapper>
        <CategoryNavigationItems
          activeNavItemIndex={activeNavItemIndex}
          items={mappedItems}
          onEnterNavigationItem={onEnterNavigationItem}
          isSearchBarExpanded={isSearchBarExpanded}
        />
        <SearchSuggestions
          isSearchBarExpanded={isSearchBarExpanded}
          expandSearchBar={expandSearchBar}
          collapseSearchBar={collapseSearchBar}
          onFocuInputChange={value => {
            setIsHintCovered(value);
          }}
        />
      </Wrapper>

      <Suspense>
        <NavigationFlyoutContentWrapper
          isOpen={
            activeNavItemIndex !== undefined &&
            !!items[activeNavItemIndex]?.flyout
          }
        >
          <NavigationFlyoutContents
            activeNavItemIndex={activeNavItemIndex}
            items={items}
          />
        </NavigationFlyoutContentWrapper>
      </Suspense>
    </TopCategoryNavigationViewWrapper>
  );
};

const TopCategoryNavigationViewMemo = memo(TopCategoryNavigationView);

export {TopCategoryNavigationViewMemo as TopCategoryNavigationView};

const useActiveFlyout = () => {
  const [activeNavItemIndex, dispatcher] = useState<undefined | number>(
    undefined,
  );
  const {setIsHintCovered} = useIsHintCovered();

  const openFlyout = useCallback((value: number) => {
    dispatcher(value);
    setIsHintCovered(true);
  }, []);
  const closeFlyout = useCallback(() => {
    dispatcher(undefined);
    setIsHintCovered(false);
  }, []);

  return {
    activeNavItemIndex,
    openFlyout,
    closeFlyout,
  };
};

const TopCategoryNavigationViewWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${NAVIGATION_HEIGHT}px;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
`;

const Wrapper = styled(RowSectionStyled)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
`;
